// LunaCard — inline insight card with the subtle luna tri-stop gradient.
// design ref: styles.css:1144 (.luna / .luna__head / .luna__head-icon /
// .luna__head-title / .luna__body) and drawers.jsx:240 (LunaCard JSX).
//
// Used inside drawers/details to surface a Luna recommendation. Border is
// p.500 (azurite-500); background layers a soft 117deg ice→indigo→coral wash
// (~5% alpha) over `neutral.25` — matches `luna.gradientCard` from theme.ts.
import { Box, Flex, Text } from "@chakra-ui/react";
import { ReactNode } from "react";

import { LunaMark } from "./LunaMark";

export interface LunaCardProps {
  /** Header label rendered next to the LunaMark. Defaults to "Luna Insight". */
  title?: ReactNode;
  /** Body content — strings, paragraphs, or lists. */
  children: ReactNode;
}

const LunaCard = ({ title = "Luna Insight", children }: LunaCardProps) => {
  return (
    <Box
      borderWidth="1px"
      borderStyle="solid"
      borderColor="p.500"
      borderRadius="component.2xl" // 12px
      p="lg" // 16px
      // Soft tri-stop gradient (ice → indigo → coral, ~5% alpha) over neutral.25.
      // Mirrors `.luna` background stack at styles.css:1148–1150.
      bg="linear-gradient(117.219deg, rgba(252,253,253,0.05) 0%, rgba(88,97,222,0.05) 50%, rgba(237,111,111,0.05) 100%), var(--chakra-colors-neutral-25)"
    >
      {/* .luna__head — flex row, 8px gap, 8px margin-bottom.
          The drawer's Luna card uses the bare brand mark (the indigo→coral
          double-chevron SVG), NOT the gradient square tile — see drawers.jsx
          LunaCard (`<LunaMark size={16} />`). */}
      <Flex align="center" gap="sm" mb="sm">
        <LunaMark size={16} />
        <Text
          as="span"
          // .luna__head-title is `var(--font-ui)` (body) at 12px / 600 — the
          // heading face (Hubot Sans) renders visibly heavier, which read as
          // "too bold". Body is the correct, lighter face here.
          fontWeight={600}
          fontSize="xs" // 12px
          color="neutral.950"
          lineHeight="1"
        >
          {title}
        </Text>
      </Flex>

      {/* .luna__body — 12px, neutral.900, line-height 1.5 */}
      <Box
        fontSize="xs"
        lineHeight="1.5"
        color="neutral.900"
        sx={{
          // .luna__body ul { padding-left: 18px; margin: 4px 0 0; }
          // .luna__body li { margin-bottom: 2px; }
          "& ul": { paddingLeft: "18px", margin: "4px 0 0" },
          "& li": { marginBottom: "2px" },
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default LunaCard;
